/**
 * The main tracker view — equivalent to the original Shared HTML but in
 * React. Single page: sync banner, header, batch tabs, stats, all the
 * cards, ingredient table, attachments, version history drawer.
 *
 * Data flow:
 *   - On mount, load /api/batches. If empty, create a default one.
 *   - Edits update local state immediately and schedule a debounced save.
 *   - A 5s polling loop hits /api/batches/changes?since=last_seen and merges
 *     remote changes that aren't ours.
 *   - Presence is heartbeated from a hook.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePresence } from '../hooks/usePresence';
import { useToast } from '../hooks/useToast';
import * as batchesApi from '../api/batches';
import { ApiError } from '../api/client';
import { defaultBatch } from '../lib/defaultBatch';
import { computeBatchStats } from '../lib/stats';
import { exportBatchCsv } from '../lib/exportCsv';
import type { Batch, BatchData, IngredientRow } from '../types';

import AppHeader from '../components/AppHeader';
import SyncBanner, { type SyncState } from '../components/SyncBanner';
import BatchTabs from '../components/BatchTabs';
import StatsRow from '../components/StatsRow';
import BatchIdentityCard from '../components/BatchIdentityCard';
import ProcessParametersCard from '../components/ProcessParametersCard';
import IngredientTable from '../components/IngredientTable';
import ObservationsCard from '../components/ObservationsCard';
import QualityTestingCard from '../components/QualityTestingCard';
import AttachmentsCard from '../components/AttachmentsCard';
import VersionHistoryDrawer from '../components/VersionHistoryDrawer';

const AUTOSAVE_DEBOUNCE_MS = 800;
const CHANGES_POLL_MS = 5_000;

export default function TrackerPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('syncing');
  const [statusMsg, setStatusMsg] = useState('Connecting…');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const lastChangeAt = useRef<number>(0);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingSaves = useRef<Set<string>>(new Set());

  const presence = usePresence(loaded);

  // ─── Initial load ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { items } = await batchesApi.listBatches();
        if (cancelled) return;
        if (items.length === 0) {
          // Seed with a default batch.
          const created = await batchesApi.createBatch(defaultBatch(1));
          if (cancelled) return;
          setBatches([created]);
          setCurrentId(created.id);
          lastChangeAt.current = created.updated_at;
        } else {
          setBatches(items);
          setCurrentId(items[0]?.id ?? null);
          lastChangeAt.current = Math.max(
            ...items.map((b) => b.updated_at),
            0,
          );
        }
        setSyncState('live');
        setStatusMsg('Synced · shared with team');
        setLoaded(true);
      } catch (e) {
        if (cancelled) return;
        setSyncState('offline');
        setStatusMsg('Offline — sign in required');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Polling change feed ───────────────────────────────────────────────
  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const r = await batchesApi.batchChanges(lastChangeAt.current);
        if (cancelled) return;
        if (r.updated.length === 0 && r.deleted.length === 0) {
          // Update the cursor to skip already-seen rows.
          lastChangeAt.current = r.server_time;
          return;
        }
        setBatches((cur) => mergeChanges(cur, r.updated, r.deleted));
        lastChangeAt.current = r.server_time;
        if (r.updated.length > 0) {
          setSyncState('live');
          setStatusMsg(
            `Updated · ${new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}`,
          );
        }
      } catch {
        // Ignored — next poll will retry.
      }
    };
    const t = setInterval(tick, CHANGES_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [loaded]);

  // ─── Save batch (debounced) ────────────────────────────────────────────
  const scheduleSave = useCallback(
    (batch: Batch) => {
      // Skip if it's still the temporary in-flight create.
      if (!batch.id) return;
      pendingSaves.current.add(batch.id);
      const existing = saveTimers.current[batch.id];
      if (existing) clearTimeout(existing);
      saveTimers.current[batch.id] = setTimeout(async () => {
        delete saveTimers.current[batch.id];
        setSyncState('syncing');
        setStatusMsg('Saving…');
        try {
          // Always read the latest snapshot — there may have been more keystrokes.
          const cur = currentSnapshotRef.current.find((b) => b.id === batch.id);
          if (!cur) return;
          const result = await batchesApi.updateBatch(
            batch.id,
            cur.data,
            cur.updated_at,
          );
          setBatches((bs) =>
            bs.map((b) =>
              b.id === batch.id ? { ...b, updated_at: result.updated_at, data: result.data } : b,
            ),
          );
          lastChangeAt.current = Math.max(lastChangeAt.current, result.updated_at);
          setSyncState('live');
          setStatusMsg(
            `Saved · ${new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}`,
          );
        } catch (e) {
          if (e instanceof ApiError && e.status === 409) {
            setSyncState('offline');
            setStatusMsg('Conflict — reloading latest…');
            show('Someone else edited this — reloaded');
            try {
              const fresh = await batchesApi.batchChanges(0);
              setBatches((cur) => mergeChanges(cur, fresh.updated, fresh.deleted));
              lastChangeAt.current = fresh.server_time;
              setSyncState('live');
              setStatusMsg('Synced · shared with team');
            } catch {}
          } else {
            setSyncState('offline');
            setStatusMsg('Save failed — will retry');
          }
        } finally {
          pendingSaves.current.delete(batch.id);
        }
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [show],
  );

  // Mirror current batches into a ref so the save closure sees fresh data.
  const currentSnapshotRef = useRef<Batch[]>([]);
  useEffect(() => {
    currentSnapshotRef.current = batches;
  }, [batches]);

  // ─── Mutations ─────────────────────────────────────────────────────────
  const current = useMemo(
    () => batches.find((b) => b.id === currentId) ?? null,
    [batches, currentId],
  );

  function patchCurrent(patch: Partial<BatchData>) {
    if (!current) return;
    setBatches((bs) =>
      bs.map((b) => (b.id === current.id ? { ...b, data: { ...b.data, ...patch } } : b)),
    );
    scheduleSave({ ...current, data: { ...current.data, ...patch } });
  }

  function setRows(rows: IngredientRow[]) {
    if (!current) return;
    setBatches((bs) =>
      bs.map((b) => (b.id === current.id ? { ...b, data: { ...b.data, rows } } : b)),
    );
    scheduleSave({ ...current, data: { ...current.data, rows } });
  }

  async function newBatch() {
    const n = batches.length + 1;
    const data = defaultBatch(n);
    try {
      const created = await batchesApi.createBatch(data);
      setBatches((bs) => [...bs, created]);
      setCurrentId(created.id);
      lastChangeAt.current = created.updated_at;
      show('New batch created');
    } catch {
      show('Failed to create batch');
    }
  }

  async function duplicateBatch() {
    if (!current) return;
    const src: BatchData = {
      ...current.data,
      metaBatchId: (current.data.metaBatchId || 'batch') + '-copy',
      batchStatus: 'draft',
    };
    try {
      const created = await batchesApi.createBatch(src);
      setBatches((bs) => [...bs, created]);
      setCurrentId(created.id);
      lastChangeAt.current = created.updated_at;
      show('Batch duplicated');
    } catch {
      show('Failed to duplicate');
    }
  }

  async function deleteCurrent() {
    if (!current) return;
    if (batches.length <= 1) {
      show('At least one batch required');
      return;
    }
    if (!confirm('Delete this batch? Cannot be undone.')) return;
    try {
      await batchesApi.deleteBatch(current.id);
      const remaining = batches.filter((b) => b.id !== current.id);
      setBatches(remaining);
      setCurrentId(remaining[0]?.id ?? null);
      show('Batch deleted');
    } catch {
      show('Delete failed');
    }
  }

  function onFocusField(fieldId: string | null) {
    presence.setFocus(current?.id ?? null, fieldId);
  }

  function onRestored(data: BatchData) {
    if (!current) return;
    setBatches((bs) =>
      bs.map((b) => (b.id === current.id ? { ...b, data } : b)),
    );
    show('Version restored');
  }

  // ─── Render ────────────────────────────────────────────────────────────
  if (!user) return null;

  const stats = current ? computeBatchStats(current.data) : null;
  const peersOnThisBatch = presence.peers.filter(
    (p) => p.batch_id && p.batch_id === current?.id,
  );

  return (
    <>
      {!loaded && (
        <div className="loading-screen">
          <div className="loading-logo">Hensley Heritage</div>
          <div className="loading-sub">Loading shared batch tracker…</div>
          <div className="loading-bar">
            <div className="loading-fill" />
          </div>
        </div>
      )}
      <SyncBanner
        state={syncState}
        statusMsg={statusMsg}
        me={user}
        peers={peersOnThisBatch}
      />
      <AppHeader
        subtitle="Shared Batch Tracker"
        actions={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => current && exportBatchCsv(current.data)} disabled={!current}>
              ⬇ Export CSV
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setDrawerOpen(true)} disabled={!current}>
              ⟲ History
            </button>
            <button className="btn btn-ghost btn-sm" onClick={duplicateBatch} disabled={!current}>
              ⧉ Duplicate
            </button>
            <button className="btn btn-ghost btn-sm" onClick={newBatch}>
              + New batch
            </button>
            <button className="btn btn-danger btn-sm" onClick={deleteCurrent} disabled={!current}>
              Delete batch
            </button>
          </>
        }
      />
      <div className="main">
        <div className="page-eyebrow">Formulation Lab</div>
        <div className="page-title">Test Batch Tracker</div>
        <div className="page-sub">Shared across your team. All changes sync automatically.</div>

        <BatchTabs batches={batches} currentId={currentId} onSelect={setCurrentId} />

        {current && stats && (
          <>
            <StatsRow
              stats={stats}
              status={current.data.batchStatus}
              onStatusChange={(s) => patchCurrent({ batchStatus: s })}
            />
            <BatchIdentityCard data={current.data} onPatch={patchCurrent} onFocusField={onFocusField} />
            <ProcessParametersCard data={current.data} onPatch={patchCurrent} onFocusField={onFocusField} />
            <IngredientTable
              rows={current.data.rows}
              percents={stats.percents}
              total={stats.totalWeight}
              onRowsChange={setRows}
              onFocusField={onFocusField}
            />
            <ObservationsCard data={current.data} onPatch={patchCurrent} onFocusField={onFocusField} />
            <QualityTestingCard data={current.data} onPatch={patchCurrent} onFocusField={onFocusField} />
            <AttachmentsCard batchId={current.id} />
          </>
        )}
      </div>

      <VersionHistoryDrawer
        open={drawerOpen}
        batchId={current?.id ?? null}
        onClose={() => setDrawerOpen(false)}
        onRestored={onRestored}
      />
    </>
  );
}

function mergeChanges(
  current: Batch[],
  updated: Batch[],
  deleted: { id: string }[],
): Batch[] {
  const map = new Map(current.map((b) => [b.id, b]));
  for (const u of updated) {
    map.set(u.id, u);
  }
  for (const d of deleted) {
    map.delete(d.id);
  }
  // Preserve original order; new entries appended in arrival order.
  const out: Batch[] = [];
  const seen = new Set<string>();
  for (const b of current) {
    if (map.has(b.id)) {
      out.push(map.get(b.id)!);
      seen.add(b.id);
    }
  }
  for (const u of updated) {
    if (!seen.has(u.id)) {
      out.push(u);
      seen.add(u.id);
    }
  }
  return out;
}
